import Tooltip from '@material-ui/core/Tooltip';
import { Skeleton } from '@material-ui/lab';
import { debounce, isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useMemo } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { track } from '~/src/dash/analytics/analytics';
import { IReport, transformReportWrapper } from '~/src/dash/api/console-api';
import {
  createReport,
  downloadIASReportData,
  downloadReportData,
  emailIASReport,
  emailReport,
  toReportAPIParams,
  updateReport,
} from '~/src/dash/apiV2/reporting/reportBuilder';
import {
  isIASReportType,
  mapStringToReportTypeID,
  ReportTypeID,
} from '~/src/dash/apiV2/reporting/reportTypes';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { ModernTextField } from '~/src/dash/components/modern-text-field';
import { borderRadius, ButtonV2, Col, Cols, spacingV2, Text } from '~/src/dash/publicaUI';
import {
  extractRouteParams,
  navigate,
  navigateToRouteWithQueryParams,
  RouteReportExisting,
  RouteReportsList,
} from '~/src/dash/router/routes';
import { publicaColors } from '~/src/dash/utils/colors';
import { encodeParams } from '~/src/dash/utils/params';
import { PID } from '~/src/dash/utils/publishers';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { EReportPriority, EReportTypeCategory } from '~/src/dash/views/reporting/common';
import { useReportBuilderStore } from '~/src/dash/views/reporting/ReportBuilder/context';

interface IReportingTopBarProps {
  isLoading: boolean;
  graphID: number;
  selectedMetrics: string[];
  selectedDimensions: string[];
  reportTypeCategory: string;
  isIasReport?: boolean;
  exchangeRate: number;
  report: IReport;
  isSchedulingActive: boolean;
  setName: (name: string) => void;
  setReport: (report: IReport) => void;
  onError: (err: any, msg: string) => void;
  onSuccess: (msg: string) => void;
  onLoading: (msg: string) => void;
}

enum EReportAction {
  Save = 'save',
  Preview = 'preview',
  Download = 'download',
  Email = 'email',
  ViewQuery = 'view the query of',
}

export const ReportingTopBar = ({
  isLoading,
  graphID,
  selectedMetrics,
  selectedDimensions,
  reportTypeCategory,
  isIasReport,
  exchangeRate,
  report,
  isSchedulingActive,
  setName,
  setReport,
  onError,
  onSuccess,
  onLoading,
}: IReportingTopBarProps) => {
  const { isSuperUser, user, session } = useBaseStore();
  const { endDate, startDate } = useReportBuilderStore();
  const { publisher } = user;
  const isIASReport = isIASReportType(+report.ReportType);
  const isNameValid = report.Name.length > 0;
  report.Name = isNameValid ? report.Name : 'Report Name';

  const formatReportName = (isNameValid: boolean, reportName: string): string => {
    return isNameValid && reportName !== 'Report Name' ? reportName : 'report-' + moment().unix();
  };

  const trackEvent = (eventTitle: string, reportSize?: number, loadTime?: number) => {
    if (!isSuperUser) {
      if (eventTitle === 'Report - Close') {
        track(
          eventTitle,
          {
            id: user.user.ID,
            is_super_user: isSuperUser,
          },
          user.user,
        );
      } else if (eventTitle === 'Report - Download') {
        track(
          eventTitle,
          {
            group_by_options: report.GroupByOptions,
            id: user.user.ID,
            is_super_user: isSuperUser,
            metrics: report.Metrics,
            period: report.Period,
            report_duration_days: moment(report.EndDate).diff(moment(report.StartDate), 'days'),
            report_id: report.ID,
            report_type: ReportTypeID[Number(report.ReportType)],
            schedule: isSchedulingActive,
            pub_id: user.publisher.ID,
            load_duration_sec: loadTime,
            reportSize,
          },
          user.user,
        );
      } else {
        track(
          eventTitle,
          {
            group_by_options: report.GroupByOptions,
            id: user.user.ID,
            is_super_user: isSuperUser,
            metrics: report.Metrics,
            period: report.Period,
            report_duration_days: moment(report.EndDate).diff(moment(report.StartDate), 'days'),
            report_id: report.ID,
            report_type: ReportTypeID[Number(report.ReportType)],
            schedule: isSchedulingActive,
            pub_id: user.publisher.ID,
          },
          user.user,
        );
      }
    }
  };

  // helper to validate report before performing save, download, email, or preview
  const validateReport = (action: EReportAction): boolean => {
    if (action === EReportAction.Save && !isNameValid) {
      onError({}, 'Report name required to save report');
      return false;
    }

    const isScheduledWithoutRecipients = report.Recipients.length === 0 && report.Active === '1';
    if (action === EReportAction.Save && isScheduledWithoutRecipients) {
      onError({}, 'To save a scheduled report, please enter some recipients');
      return false;
    }

    const isDimensionAndMetricSelected =
      selectedMetrics.length > 0 && selectedDimensions.length > 0;
    if (!isDimensionAndMetricSelected) {
      onError({}, `To ${action} a report, select at least one dimension and one metric`);
      return false;
    }

    if (
      mapStringToReportTypeID[report.ReportType] === ReportTypeID.CreativeValidationReport &&
      session.publisher.ID === PID.rarefiedAtmosphere &&
      isEmpty(report.VenueIDs)
    ) {
      onError({}, 'At least one venue ID is required');
      return false;
    }

    if (!isEmpty(report.Adomains)) {
      const limit = parseInt(report.ReportLimit, 10) || 0;
      if (isNaN(limit) || limit < 1 || limit > 500000) {
        onError({}, 'Adomain filter requires a Limit between 1 and 500,000');
        return false;
      }
    }

    return true;
  };

  // when user clicks on "Save" button, save report to DB, then navigate to the report's route
  const onSaveReport = (isNewReport: boolean) => {
    trackEvent('Report - Save');
    if (!validateReport(EReportAction.Save)) return;
    onLoading('Saving Report...');
    const save = isNewReport ? createReport : updateReport;
    save(publisher.ID, report)
      .then(([savedReport, savedAsQuickReport]) => {
        const report = transformReportWrapper(savedReport);
        if (isNewReport) {
          navigateToRouteWithQueryParams(
            RouteReportExisting,
            savedAsQuickReport ? { savedAsQuickReport } : {},
            extractRouteParams({ session, user }),
            {
              reportID: report.ID,
            },
          );
        }
        if (!isNewReport) {
          unstable_batchedUpdates(() => {
            onSuccess(savedAsQuickReport ? 'Saved as a Quick Report' : 'Report saved');
            setReport(report);
          });
        }
      })
      .catch((e) => {
        onError(e, 'Unable to save report');
      });
  };

  // when user clicks on "Email" button
  const onEmailReport = () => {
    if (!validateReport(EReportAction.Email)) return;

    report.Name = formatReportName(isNameValid, report.Name);
    trackEvent('Report - Send Email');
    onLoading('Scheduling report to be emailed...');
    const formattedParams = toReportAPIParams({
      report,
      params: {
        endDate,
        exchangeRate,
        graphID,
        priority: EReportPriority.Email,
        publisher,
        recipients: [user.user.Username],
        startDate,
      },
    });

    const email = isIASReport
      ? emailIASReport(formattedParams, formattedParams.ias_body)
      : emailReport(formattedParams);

    email
      .then((_) => onSuccess(`Got it! We'll email the report once it's ready`))
      .catch((e) => onError(e, 'Unable to send email'));
  };

  // prevent spamming "Email" button
  const debouncedEmailReport = useMemo(() => {
    return debounce(onEmailReport, 800);
  }, [onEmailReport]);

  // when user clicks on "Preview" or "Query" button, open up a new tab to view the data or the query
  const onPreviewReport = (showReportQuery: boolean = false) => {
    if (!validateReport(EReportAction.Preview)) return;
    report.Name = formatReportName(isNameValid, report.Name);
    trackEvent('Report - Preview');
    const params = toReportAPIParams({
      report,
      params: {
        endDate,
        exchangeRate,
        graphID,
        priority: EReportPriority.Unknown,
        publisher,
        recipients: [user.user.Username],
        startDate,
      },
    });
    const url = `/#/app/pub/${publisher.ID}/reports/${report.ID}/preview?${encodeParams(
      params,
    )}&snowflake_debug=${showReportQuery}`;
    window.open(url, '_blank');
  };

  // when user clicks on "Download" button, fetch the report data, then download it
  const onDownloadReport = () => {
    const startTime = moment().unix();
    if (!validateReport(EReportAction.Download)) return;

    report.Name = formatReportName(isNameValid, report.Name);
    onLoading('Downloading Report...');
    const downloadReport = isIASReport ? downloadIASReportData : downloadReportData;
    downloadReport({
      ...toReportAPIParams({
        report,
        params: {
          endDate,
          exchangeRate,
          graphID,
          priority: EReportPriority.Unknown,
          publisher,
          recipients: [user.user.Username],
          startDate,
        },
      }),
      csv: isIASReport,
    })
      .then((blob) => {
        // create an invisible anchor element that triggers the file download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.rel = 'noopner';
        a.href = url;
        a.download = `${report.Name}${isIASReport ? '.csv' : '.zip'}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        onSuccess('Download Complete');
        trackEvent('Report - Download', blob.size, moment().unix() - startTime);
      })
      .catch((e) =>
        onError(e, 'Download Failed. Please contact your administrator for further assistance.'),
      );
  };

  // prevent spamming "Download" button
  const debouncedDownloadReport = useMemo(() => {
    return debounce(onDownloadReport, 800);
  }, [onDownloadReport]);

  const onClose = () => {
    trackEvent('Report - Close');
    navigate(
      RouteReportsList,
      extractRouteParams({
        session,
        user,
      }),
    );
  };

  return (
    <Cols
      wrap
      gap={spacingV2.small}
      style={{ alignItems: 'center', justifyContent: 'space-between' }}
    >
      <Col>
        <ModernTextField
          value={report.Name}
          onChange={({ target }: React.ChangeEvent<HTMLInputElement>) => {
            setName(target.value);
          }}
          disableAllUppercase={true}
          iconSizeLarge={true}
        />
      </Col>

      {isLoading ? (
        <Col>
          <Cols wrap gap={spacingV2.huge}>
            <Col>
              <Skeleton
                variant='rect'
                animation='wave'
                width='15rem'
                height='1.5rem'
                style={{ borderRadius: borderRadius.base }}
              />
            </Col>
            <Col>
              <Skeleton
                variant='rect'
                animation='wave'
                width='8rem'
                height='1.5rem'
                style={{ borderRadius: borderRadius.base }}
              />
            </Col>
          </Cols>
        </Col>
      ) : (
        <Col>
          <Cols wrap gap={spacingV2.huge}>
            <Col marginTop={spacingV2.extraSmall} style={{ display: 'flex', alignItems: 'center' }}>
              <Cols style={{ alignItems: 'center' }}>
                {reportTypeCategory === EReportTypeCategory.QuickReports && (
                  <Col marginRight={spacingV2.small}>
                    <Tooltip title="Preview report's data in a new tab." leaveDelay={100}>
                      <div>
                        <ButtonV2
                          onClick={() => onPreviewReport(false)}
                          style={{
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                        >
                          <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                            <Col displayFlex style={{ alignItems: 'center' }}>
                              <IconV2 name='flash' small color={publicaColors.white} />
                            </Col>
                            <Col>
                              <Text sizeV2 small bold color={publicaColors.white}>
                                Preview
                              </Text>
                            </Col>
                          </Cols>
                        </ButtonV2>
                      </div>
                    </Tooltip>
                  </Col>
                )}

                {!isIasReport && (
                  <Col
                    marginRight={
                      reportTypeCategory === EReportTypeCategory.QuickReports
                        ? spacingV2.extraSmall
                        : spacingV2.small
                    }
                  >
                    <Tooltip
                      title={
                        <div>
                          Run report and email when ready. Expect longer delivery
                          <br />
                          times for larger reports.
                        </div>
                      }
                      leaveDelay={100}
                    >
                      <div>
                        <ButtonV2
                          clearBackground={reportTypeCategory === EReportTypeCategory.QuickReports}
                          onClick={debouncedEmailReport}
                          disabled={isLoading}
                          style={{
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                        >
                          <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                            <Col displayFlex style={{ alignItems: 'center' }}>
                              <IconV2
                                name='email'
                                small
                                color={
                                  reportTypeCategory === EReportTypeCategory.QuickReports
                                    ? publicaColors.blue
                                    : publicaColors.white
                                }
                              />
                            </Col>
                            <Col>
                              <Text
                                sizeV2
                                small
                                bold
                                color={
                                  reportTypeCategory === EReportTypeCategory.QuickReports
                                    ? publicaColors.blue
                                    : publicaColors.white
                                }
                              >
                                Email
                              </Text>
                            </Col>
                          </Cols>
                        </ButtonV2>
                      </div>
                    </Tooltip>
                  </Col>
                )}

                {reportTypeCategory === EReportTypeCategory.QuickReports && (
                  <Col marginRight={spacingV2.extraSmall}>
                    <Tooltip title='Get report in a CSV file now.' leaveDelay={100}>
                      <div>
                        <ButtonV2
                          clearBackground
                          onClick={debouncedDownloadReport}
                          disabled={isLoading}
                          style={{
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                        >
                          <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                            <Col displayFlex style={{ alignItems: 'center' }}>
                              <IconV2 name='download' small color={publicaColors.blue} />
                            </Col>
                            <Col>
                              <Text sizeV2 small bold color={publicaColors.blue}>
                                Download
                              </Text>
                            </Col>
                          </Cols>
                        </ButtonV2>
                      </div>
                    </Tooltip>
                  </Col>
                )}

                {isSuperUser && !isIasReport && (
                  <Col>
                    <Tooltip title='Inspect query generated by this report.' leaveDelay={100}>
                      <div>
                        <ButtonV2
                          clearBackground
                          onClick={() => onPreviewReport(true)}
                          style={{
                            padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                          }}
                        >
                          <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                            <Col displayFlex style={{ alignItems: 'center' }}>
                              <IconV2 name='code' small color={publicaColors.blue} />
                            </Col>
                            <Col>
                              <Text sizeV2 small bold color={publicaColors.blue}>
                                Query *
                              </Text>
                            </Col>
                          </Cols>
                        </ButtonV2>
                      </div>
                    </Tooltip>
                  </Col>
                )}
              </Cols>
            </Col>

            <Col marginTop={spacingV2.extraSmall}>
              <Cols style={{ alignItems: 'center' }}>
                <Col noPadding>
                  <ButtonV2
                    clearBackground
                    onClick={() => onSaveReport(report.ID === '0')}
                    style={{
                      padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                    }}
                  >
                    <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                      <Col displayFlex style={{ alignItems: 'center' }}>
                        <IconV2 name='save' small color={publicaColors.blue} />
                      </Col>
                      <Col>
                        <Text sizeV2 small bold color={publicaColors.blue}>
                          {isIasReport ? 'Save and Email' : 'Save'}
                        </Text>
                      </Col>
                    </Cols>
                  </ButtonV2>
                </Col>
                <Col noPadding>
                  <ButtonV2
                    clearBackground
                    onClick={onClose}
                    style={{
                      padding: `${spacingV2.extraSmall} ${spacingV2.small}`,
                    }}
                  >
                    <Cols gap={spacingV2.extraSmall} style={{ alignItems: 'center' }}>
                      <Col displayFlex style={{ alignItems: 'center' }}>
                        <IconV2 name='close' small color={publicaColors.blue} />
                      </Col>
                      <Col>
                        <Text sizeV2 small bold color={publicaColors.blue}>
                          Close
                        </Text>
                      </Col>
                    </Cols>
                  </ButtonV2>
                </Col>
              </Cols>
            </Col>
          </Cols>
        </Col>
      )}
    </Cols>
  );
};
